/**
 * AgentActivityPanel – qué ha hecho el agente de este equipo y qué ha
 * respondido, dentro de la propia ficha.
 *
 * Responde a dos preguntas que antes obligaban a entrar por SSH/RDP al equipo:
 *
 *   1. **¿Se está ejecutando lo que pido?** Lista los comandos remotos
 *      (`rescan`, `exec`, `update_agent`…) con su estado y sus tiempos.
 *   2. **¿Qué dijo el agente?** Cada ejecución se abre y enseña `stdout`,
 *      `stderr` y el error tal cual, sin recortes.
 *
 * El botón «Ver log del agente» encola un `exec` que localiza
 * `cmdb-agent.log` y devuelve sus últimas 200 líneas; el resultado se lee en
 * la misma lista, como una ejecución más. No necesita cambiar el agente.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  DocumentTextIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';
import HelpDot from '../academy/HelpDot';
import useAgentUpdateStatus from '../hooks/useAgentUpdateStatus';
import { versionAtLeast } from '../utils/version';

// Busca el log en las rutas de instalación conocidas y, si no está, lo busca
// bajo Program Files/ProgramData. Comillas simples dentro del -Command para no
// pelearse con cmd.exe.
//
// `tail` depende de la versión del agente: hasta 2.6.3 la respuesta de `exec`
// se recorta a 4 KB en el propio agente, así que 200 líneas no llegarían.
function buildAgentLogCommand(tail) {
  return [
    'powershell -NoProfile -Command "',
    "$c = 'C:\\Program Files\\CMDB Agent\\cmdb-agent.log',",
    "'C:\\Program Files (x86)\\CMDB Agent\\cmdb-agent.log',",
    "(Join-Path $env:ProgramData 'CMDB Agent\\cmdb-agent.log'); ",
    '$p = $c | Where-Object { Test-Path $_ } | Select-Object -First 1; ',
    'if (-not $p) { $p = Get-ChildItem \'C:\\Program Files\',\'C:\\Program Files (x86)\',$env:ProgramData ',
    "-Filter 'cmdb-agent.log' -Recurse -ErrorAction SilentlyContinue | Select-Object -First 1 -ExpandProperty FullName }; ",
    `if ($p) { Write-Output ('LOG: ' + $p); Get-Content -LiteralPath $p -Tail ${tail} } else { Write-Output 'No se encontro cmdb-agent.log en este equipo' }`,
    '"',
  ].join('');
}

const ACTIVE_STATUSES = new Set(['pending', 'received', 'running']);

const STATUS_META = {
  pending: { label: 'En cola', pill: 'sf2-pill--info' },
  received: { label: 'Recibido', pill: 'sf2-pill--info' },
  running: { label: 'Ejecutando', pill: 'sf2-pill--wa' },
  done: { label: 'Hecho', pill: 'sf2-pill--ok' },
  error: { label: 'Error', pill: 'sf2-pill--bad' },
  cancelled: { label: 'Cancelado', pill: 'sf2-pill--wa' },
};

const COMMAND_LABELS = {
  exec: 'Comando',
  rescan: 'Rescan',
  update_agent: 'Actualizar agente',
  hypervisor_sync: 'Sync hipervisor',
  ad_audit: 'Auditoría AD',
  network_scan: 'Barrido de red',
};

/** Primera versión del agente que se actualiza sola desde la ficha.
 *
 * Antes de la 2.6.6 el comando lanzaba `cmdb-service.exe update`, un subcomando
 * que ese binario no tiene: el equipo contestaba «Usage: cmdb-service.exe
 * install|uninstall|start|stop|status» y la actualización nunca empezaba.
 */
const MIN_AUTO_UPDATE_VERSION = '2.6.6';

function commandLabel(command) {
  return COMMAND_LABELS[command] || command || '—';
}

function parseDate(value) {
  if (!value) return null;
  const raw = String(value);
  const parsed = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatMoment(value) {
  const parsed = parseDate(value);
  return parsed ? parsed.toLocaleString('es-ES') : '—';
}

function formatDuration(from, to) {
  const start = parseDate(from);
  const end = parseDate(to);
  if (!start || !end) return '—';
  const seconds = Math.max(0, Math.round((end.getTime() - start.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

/** Texto legible de la respuesta del agente, sea la que sea. */
function resultText(command) {
  const result = command?.result || {};
  const chunks = [];
  if (result.error) chunks.push(`ERROR: ${result.error}`);
  if (result.status && result.status !== 'done') chunks.push(`Estado: ${result.status}`);
  if (result.target_version) chunks.push(`Versión objetivo: ${result.target_version}`);
  if (result.agent_version) chunks.push(`Versión agente: ${result.agent_version}`);
  if (result.exit_code !== undefined && result.exit_code !== null) {
    chunks.push(`Código de salida: ${result.exit_code}`);
  }
  if (result.stdout) chunks.push(result.stdout);
  if (result.stderr) chunks.push(`--- stderr ---\n${result.stderr}`);
  if (!chunks.length) {
    return command?.status === 'pending' || command?.status === 'running'
      ? 'Esperando respuesta del agente…'
      : 'El agente no devolvió contenido.';
  }
  return chunks.join('\n');
}

export default function AgentActivityPanel({
  companyId,
  ciType,
  ciId,
  agentId,
  agentVersion = null,
  agentStatus = 'unknown',
  lastSeenAt = null,
  latestVersion = null,
}) {
  const [commands, setCommands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [enqueueing, setEnqueueing] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [confirmUpdateOpen, setConfirmUpdateOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  // El historial de ejecuciones nace plegado: solo se ve lo que está en curso.
  const [showAll, setShowAll] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!companyId || !agentId) return;
    setLoading(true);
    try {
      const payload = await api.getAgentCommands(companyId, { agent_id: agentId });
      if (!mountedRef.current) return;
      setCommands(Array.isArray(payload?.commands) ? payload.commands : []);
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      const message = e?.message || 'No se pudo leer el historial del agente';
      // El endpoint es de administración: a un editor se le dice, no se le
      // enseña un error rojo de red.
      setError(/403|denied|permis/i.test(message) ? 'Solo un administrador puede ver la actividad del agente.' : message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [companyId, agentId]);

  useEffect(() => {
    load();
  }, [load]);

  const hasActive = useMemo(
    () => commands.some((cmd) => ACTIVE_STATUSES.has(cmd?.status)),
    [commands],
  );

  useEffect(() => {
    if (!hasActive) return undefined;
    const timer = window.setInterval(load, 8000);
    return () => window.clearInterval(timer);
  }, [hasActive, load]);

  const requestAgentLog = async () => {
    if (!companyId || !agentId) return;
    setEnqueueing(true);
    setError(null);
    try {
      // 2.6.4+ levanta el recorte de `exec` a 32 KB y caben 200 líneas.
      const tail = versionAtLeast(agentVersion, '2.6.4') ? 200 : 40;
      await api.enqueueAgentCommand(companyId, {
        agent_id: agentId,
        command: 'exec',
        ci_type: ciType,
        ci_id: ciId,
        payload: { cmd: buildAgentLogCommand(tail) },
      });
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo pedir el log al agente');
    } finally {
      if (mountedRef.current) setEnqueueing(false);
    }
  };

  /** Vuelve a pedir el inventario completo: sistema, discos y NICs. */
  const requestRescan = async () => {
    if (!companyId || !agentId) return;
    setRescanning(true);
    setError(null);
    setNotice(null);
    try {
      await api.enqueueAgentCommand(companyId, {
        agent_id: agentId,
        command: 'rescan',
        ci_type: ciType,
        ci_id: ciId,
        payload: {},
      });
      setNotice(
        'Rescan encolado. El agente lo ejecutará en su próximo sondeo (hasta ~60 s).',
      );
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo encolar el rescan');
    } finally {
      if (mountedRef.current) setRescanning(false);
    }
  };

  /** Descarga e instala la versión publicada en el CMDB. */
  const requestAgentUpdate = async () => {
    if (!companyId || !ciId) return;
    if (!autoactualizacionSoportada) {
      // Mejor decirlo antes de confirmar que dejar que el agente conteste
      // «Usage: cmdb-service.exe install|uninstall|…» y el operador se quede
      // mirando un error que no puede resolver desde aquí.
      setConfirmUpdateOpen(false);
      setNotice(null);
      setError(
        `El agente de este equipo va por la v${agentVersion} y no puede actualizarse `
        + 'desde la ficha. Instala el MSI o el EXE de la versión publicada '
        + '(Ajustes → Agente CMDB) o despliégalo por GPO. Desde la '
        + `${MIN_AUTO_UPDATE_VERSION} el botón funciona solo.`,
      );
      return;
    }
    setConfirmUpdateOpen(true);
  };

  /** Confirmado en el diálogo: encola la actualización. */
  const confirmAgentUpdate = async () => {
    if (!companyId || !ciId) return;
    setConfirmUpdateOpen(false);
    setUpdating(true);
    setError(null);
    setNotice(null);
    try {
      await api.sendAgentUpdateCommand(companyId, { ci_type: ciType, ci_id: ciId });
      setNotice(
        'Actualización encolada. La cabecera de la ficha mostrará «Actualizando…» y '
        + 'pasará a «Actualizado» cuando el equipo informe de la versión nueva.',
      );
      await load();
    } catch (e) {
      setError(e?.message || 'No se pudo encolar la actualización del agente');
    } finally {
      if (mountedRef.current) setUpdating(false);
    }
  };

  const statusPill = agentStatus === 'online'
    ? 'sf2-pill--ok'
    : agentStatus === 'offline'
      ? 'sf2-pill--bad'
      : '';
  // La ficha y la lista tienen que decir lo mismo: se compara con el mismo util.
  const versionDesfasada = Boolean(
    agentVersion && latestVersion && versionAtLeast(latestVersion, agentVersion)
      && latestVersion !== agentVersion,
  );
  // Mientras la actualización está viva se enseña el salto animado
  // «v2.6.8 → v2.6.9»; el estado sale del mismo endpoint que pinta la cabecera.
  const updateStatus = useAgentUpdateStatus(companyId, { ciType, ciId, agentId });
  const actualizando = Boolean(
    updateStatus && ['updating', 'unconfirmed'].includes(updateStatus.state),
  );
  const versionObjetivo = updateStatus?.target_version || latestVersion;
  const autoactualizacionSoportada = Boolean(
    !agentVersion || versionAtLeast(agentVersion, MIN_AUTO_UPDATE_VERSION),
  );

  return (
    <div className="dv-section">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <h3 className="dv-section-title" style={{ margin: 0 }}>Agente CMDB</h3>
        <HelpDot topic="listados.agent_activity" label="Ayuda: log y ejecuciones del agente" />
        <span className={`sf2-pill ${statusPill}`}>
          {agentStatus === 'online' ? 'Online' : agentStatus === 'offline' ? 'Offline' : 'Sin agente'}
        </span>
        {agentVersion && !actualizando && (
          <span className="sf2-pill sf2-pill--ac">v{agentVersion}</span>
        )}
        {actualizando && (
          <span className="sf2-version-flow" title={`Instalando ${versionObjetivo || 'la versión publicada'}`}>
            <span className="sf2-pill sf2-pill--ac">v{agentVersion || '?'}</span>
            <ArrowRightIcon className="sf2-version-flow__arrow" aria-hidden="true" />
            <span className="sf2-pill sf2-pill--wa">v{versionObjetivo || '…'}</span>
          </span>
        )}
        {versionDesfasada && !actualizando && (
          <>
            <span
              className="sf2-pill sf2-pill--wa"
              title={`Publicada la ${latestVersion}. Este equipo va por detrás.`}
            >
              → v{latestVersion}
            </span>
          </>
        )}
        {!autoactualizacionSoportada && !actualizando && (
          <span
            className="sf2-pill sf2-pill--bad"
            title={
              `Esta versión (v${agentVersion}) solo sabe actualizarse con el `
              + `instalador. Desde la ${MIN_AUTO_UPDATE_VERSION} la actualización `
              + 'remota funciona sin tocar el equipo.'
            }
          >
            Sin autoactualización
          </span>
        )}
        <span className="sf2-hint" style={{ margin: 0 }}>
          Último contacto: {formatMoment(lastSeenAt)}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="dv-btn dv-btn-secondary"
            onClick={requestRescan}
            disabled={rescanning || !agentId}
            title="Pide al agente que vuelva a recoger sistema, discos y NICs"
          >
            <SparklesIcon /> {rescanning ? 'Encolando…' : 'Rescan completo'}
          </button>
          <button
            type="button"
            className="dv-btn dv-btn-secondary"
            onClick={requestAgentUpdate}
            disabled={updating || !ciId || !agentId}
            title={
              autoactualizacionSoportada
                ? 'Descarga la versión publicada en el CMDB y la instala en el equipo'
                : `Este agente (v${agentVersion}) no sabe actualizarse solo: `
                  + 'instala el MSI o el EXE a mano'
            }
          >
            <ArrowUpTrayIcon /> {updating ? 'Encolando…' : 'Actualizar agente'}
          </button>
          <button
            type="button"
            className="dv-btn dv-btn-secondary"
            onClick={load}
            disabled={loading || !agentId}
            title="Volver a leer el historial"
          >
            <ArrowPathIcon /> Refrescar
          </button>
          <button
            type="button"
            className="dv-btn dv-btn-secondary"
            onClick={requestAgentLog}
            disabled={enqueueing || !agentId}
            title="Pide al agente las últimas 200 líneas de cmdb-agent.log"
          >
            <DocumentTextIcon /> {enqueueing ? 'Encolando…' : 'Ver log del agente'}
          </button>
        </div>
      </div>

      <p className="sf2-hint" style={{ marginTop: 0 }}>
        Todo lo que el CMDB le ha pedido a este agente y lo que ha contestado. Pulsa una
        ejecución para ver su salida completa.
      </p>
      <p className="sf2-hint" style={{ marginTop: 0 }}>
        <b>Rescan completo</b> rehace el inventario del equipo (sistema, discos, volúmenes y
        tarjetas de red) y lo sube al CMDB. Úsalo cuando falte un dato o hayas cambiado hardware.
      </p>

      {error && (
        <div className="sf2-pill sf2-pill--bad" style={{ display: 'inline-block', marginBottom: 10 }}>
          {error}
        </div>
      )}

      {notice && (
        <div className="sf2-pill sf2-pill--info" style={{ display: 'inline-block', marginBottom: 10 }}>
          {notice}
        </div>
      )}

      {!agentId && (
        <p className="sf2-hint">Este equipo no tiene agente enlazado: no hay actividad que mostrar.</p>
      )}

      {agentId && !commands.length && !loading && !error && (
        <p className="sf2-hint">Todavía no se ha ejecutado ningún comando en este agente.</p>
      )}

      {commands.length > 0 && (() => {
        const activeCommands = commands.filter((cmd) => ACTIVE_STATUSES.has(cmd?.status));
        const visibleCommands = activeCommands.length > 0
          ? activeCommands
          : (showAll ? commands : []);
        return (
          <>
            {visibleCommands.length === 0 ? (
              <p className="sf2-hint">
                Sin ejecuciones en curso. El historial está plegado para no quitar sitio.
              </p>
            ) : (
              <div style={{ display: 'grid', gap: 6 }}>
                {visibleCommands.map((cmd) => {
            const meta = STATUS_META[cmd.status] || { label: cmd.status || '—', pill: '' };
            const open = openId === cmd.id;
            return (
              <div
                key={cmd.id}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  background: 'var(--bg-card)',
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : cmd.id)}
                  aria-expanded={open}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    padding: '0.55rem 0.75rem',
                    border: 0,
                    background: 'transparent',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    flexWrap: 'wrap',
                  }}
                >
                  {open ? <ChevronDownIcon style={{ width: 16, height: 16 }} /> : <ChevronRightIcon style={{ width: 16, height: 16 }} />}
                  <b style={{ fontSize: '0.85rem' }}>{commandLabel(cmd.command)}</b>
                  <span className={`sf2-pill ${meta.pill}`}>{meta.label}</span>
                  <span className="sf2-hint" style={{ margin: 0 }}>{formatMoment(cmd.created_at)}</span>
                  <span className="sf2-hint" style={{ margin: 0 }}>
                    {formatDuration(cmd.created_at, cmd.executed_at)}
                  </span>
                  <span className="sf2-hint" style={{ margin: 0, marginLeft: 'auto' }}>
                    {cmd.executed_at ? 'ver salida' : 'sin respuesta todavía'}
                  </span>
                </button>
                {open && (
                  <pre
                    style={{
                      margin: 0,
                      padding: '0.7rem 0.85rem',
                      borderTop: '1px solid var(--border)',
                      background: 'var(--bg-subtle, rgba(15, 23, 42, 0.04))',
                      fontSize: '0.74rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 380,
                      overflow: 'auto',
                    }}
                  >
                    {resultText(cmd)}
                  </pre>
                )}
              </div>
            );
          })}
              </div>
            )}
            {!showAll && commands.length > 0 && (
              <button
                type="button"
                className="dv-btn dv-btn-secondary"
                onClick={() => setShowAll(true)}
                style={{ marginTop: 8 }}
              >
                Ver historial completo ({commands.length})
              </button>
            )}
            {showAll && (
              <button
                type="button"
                className="dv-btn dv-btn-secondary"
                onClick={() => setShowAll(false)}
                style={{ marginTop: 8 }}
              >
                Ocultar historial
              </button>
            )}
          </>
        );
      })()}

      {confirmUpdateOpen && (
        <Dialog
          title="Actualizar el agente"
          icon={<ArrowUpTrayIcon style={{ width: 20, height: 20 }} />}
          onClose={() => setConfirmUpdateOpen(false)}
          busy={updating}
          footer={(
            <>
              <button
                type="button"
                className="dv-btn dv-btn-secondary"
                onClick={() => setConfirmUpdateOpen(false)}
                disabled={updating}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="dv-btn dv-btn-primary"
                onClick={confirmAgentUpdate}
                disabled={updating}
              >
                {updating ? 'Encolando…' : 'Actualizar'}
              </button>
            </>
          )}
        >
          <p style={{ margin: '0 0 10px' }}>
            Se descargará la versión del agente publicada en el CMDB
            {agentVersion ? ` (ahora tiene la ${agentVersion})` : ''} y se instalará
            en el equipo.
          </p>
          <p style={{ margin: 0 }}>
            El servicio se reiniciará y el equipo quedará unos segundos sin latido.
            La ficha no dará la actualización por buena hasta que el equipo vuelva
            con la versión nueva.
          </p>
        </Dialog>
      )}
    </div>
  );
}
